import {
  TdsChip,
  TdsDropdown,
  TdsDropdownOption,
  TdsSpinner,
  TdsTooltip,
} from '@scania/tegel-react'
import styled from 'styled-components'
import {
  SvgInfo,
  SvgScaniaSuper,
  SvgTick,
} from '../../../components/SvgImports'
import { CustomScrollbarMixin } from '../../../css/CustomScrollbarMixin'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  openGvPanel,
  setGvSidePanelContent,
  setReadMoreNode,
} from '../../../store/sidePanelSlice'
import { SidePanelContent } from '../../../store/types'
import { getMenuState, setMenuTreeGv } from '../../../store/menuSlice'
import {
  getSessionInitDataState,
  getUserSelectionTimestamp,
} from '../../../store/sessionDataSlice'
import { useClient } from '../../../utils/useClient'
import { useCallback, useLayoutEffect, useRef } from 'react'
import { GuidedViewMenuLeafNode } from '../../../api/generated'
import {
  ScaniaAdobeEventId,
  ScaniaAdobeTrackingClickBmReadMoreEvent,
  ScaniaAdobeTrackingMenuClickEvent,
  pushAdobeEvent,
} from '../../../utils/adobeAnalytics'
import { BreakpointWidthPx } from '../../../css/BreakpointWidthPx'
import { TextId, useTextsV2 } from '../../../utils/useTexts'
import { BreakpointHeightPx } from '../../../css/BreakpointHeightPx'
import { Size2d } from '../../../types/Size2d'
import { blinkKeyframesBorder } from '../MenuBlinkAnimationBorder'
import { ViewAllOrLessOptionsButton } from './ViewAllOrLessOptionsButtonMobile'
import {
  getUrlCidBevSemiliveParameters,
  UrlParamMode,
} from '../../../utils/getUrlCidParameter'
import { TestElementTypeId } from '../../../types/TestAttributeId'
import { getLatestConfigChangeResult } from '../../../store/truckInfoSlice'

const MenuContentRoot = styled.div`
  ${CustomScrollbarMixin}
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  padding-right: 24px;
  overflow-y: scroll;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    padding: 16px;
    overflow-y: unset;
  }
`

const Header = styled.div`
  margin-bottom: 8px;
  margin-top: 8px;
  font-size: 14px;
  font-weight: bold;

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    font-size: 14px;
  }
`

// Menu level 2
const SwitchWrapper = styled.div`
  width: 100%;
  border-radius: 4px;
  display: flex;
  align-items: center;
  background-color: var(--tds-grey-100);
  padding: 4px;
  margin-bottom: 16px;

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    margin-bottom: 16px;
  }
`

interface SwitchProps {
  $isSelected: boolean
}

const SwitchButton = styled.div<SwitchProps>`
  padding-left: 16px;
  padding-right: 16px;
  padding-top: 8px;
  padding-bottom: 8px;
  display: flex;
  flex: 3;
  justify-content: center;
  border-radius: ${(props) => (props.$isSelected ? '2px' : '')};
  background-color: ${(props) =>
    props.$isSelected ? 'var(--tds-white)' : 'var(--tds-grey-100)'};
  color: ${(props) =>
    props.$isSelected ? 'var(--tds-grey-958)' : 'var(--tds-grey-700)'};
  font-weight: bold;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: var(--tds-grey-958);
      cursor: pointer;
      opacity: 100%;
    }
  }

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    padding-left: 8px;
    padding-right: 8px;
    padding-top: 4px;
    padding-bottom: 4px;
    font-size: 13px;
  }
`

const SelectedOptionWrapper = styled.div`
  display: flex;
  margin-top: 32px;
  margin-bottom: 16px;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }
`

const SelectedOptionText = styled.div`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-weight: bold;
`

// Menu level 3
const ChipWrapper = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  max-width: 100%;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    display: none;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    margin-top: 8px;
    gap: 8px;
  }
`

const CardsWrapper = styled.div``

interface CardProps {
  $isSelected: boolean
  $isHighlighted: boolean
}

const OptionCard = styled.div<CardProps>`
  width: 100%;
  height: 120px;
  margin-bottom: 8px;
  border-radius: 4px;
  border: ${(props) => (props.$isSelected ? '2px' : '1px')} solid
    ${(props) =>
      props.$isSelected ? 'var(--tds-information)' : 'var(--tds-grey-300)'};
  display: flex;
  justify-content: center;
  padding: 16px;

  animation-duration: 500ms;
  animation-iteration-count: 3;
  animation-name: ${(props) =>
    props.$isHighlighted ? blinkKeyframesBorder : 'unset'};

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    height: 60px;
    padding: 8px;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    height: 88px;
    padding: 12px;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      ${({ $isSelected }) => !$isSelected && 'cursor: pointer;'}
    }
  }
`

const CardImage = styled.div`
  width: 130px;
  min-width: 130px;
  height: 100%;
  display: flex;
  justify-content: flex-end;
  padding-right: 8px;

  img {
    max-height: 100%;
    max-width: 100%;
    object-fit: contain;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    width: 80px;
    min-width: 80px;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    width: 100px;
    min-width: 100px;
  }
`

const CardTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: center;
  overflow: hidden;
  padding-right: 8px;
`

const CardTextHeader = styled.div`
  height: 18px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 16px;
  margin-bottom: 4px;

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    font-size: 14px;
    margin-bottom: 2px;
  }
`

const CardTextWrapper = styled.div`
  @media screen and (max-width: ${BreakpointWidthPx.Medium}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    display: none;
  }
`

const CardText = styled.div`
  font-size: 11px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    -webkit-line-clamp: 1;
  }
`

const CardStatusWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 32px;
  min-width: 32px;
  justify-content: center;

  svg {
    height: 24px;
  }
`

const ReadMoreAndSuperWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 6px;

  @media screen and (max-width: ${BreakpointWidthPx.Large}px) {
    padding-top: 3px;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Medium}px) and (min-width: ${BreakpointWidthPx.Small}px) {
    flex-direction: column;
    gap: 4px;
    align-items: flex-start;
    padding-top: 0px;
  }
`

const CardReadMore = styled.div`
  display: flex;
  align-items: end;
  gap: 4px;
  font-size: 10px;
  height: 16px;
  align-items: center;
  width: fit-content;
  text-decoration: underline;

  svg {
    height: 16px;
    width: 16px;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    margin: 0px;
  }
`

const CardSuper = styled.div`
  background-color: var(--tds-blue-500);
  border-radius: 16px;
  width: fit-content;
  padding: 2px 12px;

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    padding: 1px 10px;
  }

  svg {
    height: 12px;

    @media screen and (max-width: ${BreakpointWidthPx.Large}px),
      screen and (max-height: ${BreakpointHeightPx.Large}px) {
      height: 9px;
    }

    path {
      fill: white;
    }
  }
`

const DropdownWrapper = styled.div`
  display: none;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    display: unset;
  }
`

interface NonColourProps {
  parentSize: Size2d
  handleItemSelection: (id: string, text: string) => void
  highlightedId: string | null
}

export function MenuContentNonColour({
  parentSize,
  handleItemSelection,
  highlightedId,
}: NonColourProps): JSX.Element {
  const dispatch = useAppDispatch()
  const t2 = useTextsV2()
  const sessionInitData = useAppSelector(getSessionInitDataState)
  const client = useClient()
  const userSelectionTimestamp = useAppSelector(getUserSelectionTimestamp)
  const { currentlyLoadingId } = useAppSelector(getMenuState)
  const scrollToItemOnHighlightedRef = useRef<HTMLDivElement | null>(null)
  const scrollToItemOnSelectedClickRef = useRef<HTMLDivElement | null>(null)
  const menuTreeGv = useAppSelector(getMenuState).menuResultGv?.menuTree
  const selectedMotorNode =
    useAppSelector(getMenuState).menuResultGv?.selectedMotorNode
  const menuLevel2 = menuTreeGv?.subMenus
    ? menuTreeGv?.subMenus.find((menu) => menu.isOpen === true)?.subMenus
    : null
  const menuLevel3 = menuLevel2
    ? menuLevel2.find((menu) => menu.isOpen === true)?.subMenus
    : null
  const menuLevel4 = menuLevel3
    ? menuLevel3.find((menu) => menu.isOpen === true)?.leafItems
    : null
  const selectedLeafItem = menuLevel4?.find((item) => item.isSelected === true)
  const isMotorMenu = menuLevel2
    ? menuLevel2.find((menu) => menu.isOpen === true)?.id ===
      'SUBMENU_GV_COMBUSTION'
    : false
  const isElecticMotorMenu = menuLevel2
    ? menuLevel2.find((menu) => menu.isOpen === true)?.id ===
      'SUBMENU_GV_ELECTRIC'
    : false
  const urlCidParams = getUrlCidBevSemiliveParameters(UrlParamMode.ExcludeBev)
  const showFpcCodeOnHover = urlCidParams.has('fpc')
  const superEngines = useAppSelector(getLatestConfigChangeResult)?.superEngines

  const handleReadmoreClick = useCallback(
    async (
      e: React.MouseEvent<HTMLDivElement, MouseEvent>,
      node: GuidedViewMenuLeafNode,
    ) => {
      e.stopPropagation()
      e.preventDefault()

      dispatch(setReadMoreNode({ id: node.id, title: node.shortText || null }))
      dispatch(openGvPanel())
      dispatch(setGvSidePanelContent(SidePanelContent.readMore))

      const trackingEvent: ScaniaAdobeTrackingClickBmReadMoreEvent = {
        event: ScaniaAdobeEventId.BmReadMoreClick,
      }
      pushAdobeEvent(trackingEvent)
    },
    [dispatch],
  )

  const handleSubMenuClick = (menuId: string, text: string) => {
    if (!sessionInitData?.sessionId) {
      return
    }
    if (!client) {
      return
    }
    const asyncWrapper = async () => {
      const newMenuState = await client.exploreMenu({
        sessionId: sessionInitData.sessionId,
        menuId: menuId,
        showAllConfigMenu: false,
      })
      if (newMenuState.success) {
        dispatch(setMenuTreeGv(newMenuState.success))

        const trackingEvent: ScaniaAdobeTrackingMenuClickEvent = {
          event: ScaniaAdobeEventId.MenuClick,
          eventInfo: {
            optionClickedId: menuId,
            optionClickedText: text,
          },
        }
        pushAdobeEvent(trackingEvent)
      }
    }
    asyncWrapper()
  }

  const handleSubMenuDropdownSelection = (
    evt: CustomEvent<{ name: string; value: string }>,
  ) => {
    if (!sessionInitData?.sessionId) {
      return
    }
    if (!client) {
      return
    }
    const selectedSubMenuId = evt.detail.value
    const asyncWrapper = async () => {
      const newMenuState = await client.exploreMenu({
        sessionId: sessionInitData.sessionId,
        menuId: selectedSubMenuId,
        showAllConfigMenu: false,
      })
      if (newMenuState.success) {
        dispatch(setMenuTreeGv(newMenuState.success))

        const trackingEvent: ScaniaAdobeTrackingMenuClickEvent = {
          event: ScaniaAdobeEventId.MenuClick,
          eventInfo: {
            optionClickedId: selectedSubMenuId,
            optionClickedText: '',
          },
        }
        pushAdobeEvent(trackingEvent)
      }
    }
    asyncWrapper()
  }

  const capitalizeFirstLetter = (text: string | null | undefined) => {
    if (!text) {
      return null
    }
    const capitalizedText = text.charAt(0).toUpperCase() + text.slice(1)
    return capitalizedText
  }

  // Scroll down to highlighted item
  useLayoutEffect(() => {
    const scrollDelayMs = 100
    setTimeout(() => {
      if (scrollToItemOnHighlightedRef.current) {
        scrollToItemOnHighlightedRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }
    }, scrollDelayMs)
  }, [highlightedId])

  // Scroll down to selected item
  const handleSelectedClick = () => {
    if (scrollToItemOnSelectedClickRef.current) {
      scrollToItemOnSelectedClickRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }

  return (
    <MenuContentRoot>
      <ViewAllOrLessOptionsButton
        parentSize={parentSize}
        data-test-element-type={TestElementTypeId.GuidedViewViewAllButton}
      />
      <Header>{t2(TextId.HEADER_GV_PRODUCT_TYPE)}</Header>
      <SwitchWrapper>
        {menuLevel2 &&
          menuLevel2.map((menuItem) => {
            return (
              <SwitchButton
                $isSelected={menuItem.isOpen}
                key={menuItem.id}
                onClick={() =>
                  handleSubMenuClick(menuItem.id, menuItem.shortText || '')
                }
                id={'switchNonColor' + menuItem.id}
                data-test-element-type={
                  TestElementTypeId.GuidedViewSwitchButton
                }
                data-test-etel-variant-id={menuItem.id}
              >
                {showFpcCodeOnHover && (
                  <TdsTooltip
                    placement="bottom"
                    selector={'#switchNonColor' + menuItem.id}
                    text={menuItem.id}
                  />
                )}
                {menuItem.shortText ? menuItem.shortText : menuItem.id}
              </SwitchButton>
            )
          })}
      </SwitchWrapper>

      {!isElecticMotorMenu && (
        <Header>{t2(TextId.HEADER_GV_PRODUCT_OPTIONS)}</Header>
      )}
      <ChipWrapper>
        {menuLevel3 &&
          menuLevel3.map((menuItem) => {
            return (
              <TdsChip
                type="radio"
                onClick={() =>
                  handleSubMenuClick(menuItem.id, menuItem.shortText || '')
                }
                key={menuItem.id}
                checked={menuItem.isOpen ? true : false}
                size={parentSize.width < BreakpointWidthPx.Small ? 'sm' : 'lg'}
                id={'chipNonColor' + menuItem.id}
                data-test-element-type={TestElementTypeId.GuidedViewChip}
                data-test-etel-variant-id={menuItem.id}
              >
                <span slot="label">
                  {menuItem.shortText ? menuItem.shortText : menuItem.id}
                </span>
                {showFpcCodeOnHover && (
                  <TdsTooltip
                    placement="bottom"
                    selector={'#chipNonColor' + menuItem.id}
                    text={menuItem.id}
                  />
                )}
              </TdsChip>
            )
          })}
      </ChipWrapper>
      {menuLevel3 && !isElecticMotorMenu && (
        <DropdownWrapper>
          <TdsDropdown
            size="sm"
            open-direction="auto"
            name="dropdown"
            defaultValue={
              isMotorMenu
                ? menuLevel3.find((i) => i.isOpen === true)?.id
                : menuLevel3[0].id || undefined
            }
            onTdsChange={handleSubMenuDropdownSelection}
            key={menuLevel3.toString()}
            data-test-element-type={TestElementTypeId.GuidedViewDropDownLevel3}
          >
            {menuLevel3.map((menuItem) => (
              <TdsDropdownOption
                key={menuItem.id}
                value={menuItem.id}
                data-test-etel-variant-id={menuItem.id}
              >
                {menuItem.shortText}
              </TdsDropdownOption>
            ))}
          </TdsDropdown>
        </DropdownWrapper>
      )}
      <SelectedOptionWrapper onClick={handleSelectedClick}>
        <div>{t2(TextId.GV_SELECTED)}&nbsp;</div>
        <SelectedOptionText>
          {capitalizeFirstLetter(
            isMotorMenu || isElecticMotorMenu
              ? selectedMotorNode?.shortText
              : selectedLeafItem?.shortText,
          )}
        </SelectedOptionText>
      </SelectedOptionWrapper>

      <CardsWrapper>
        {menuLevel4 &&
          menuLevel4.map((leafItem, i) => {
            return (
              <OptionCard
                $isSelected={leafItem.isSelected}
                $isHighlighted={leafItem.id === highlightedId}
                key={leafItem.id + userSelectionTimestamp?.toString()}
                onClick={
                  leafItem.isSelected
                    ? () => {}
                    : () =>
                        handleItemSelection(
                          leafItem.id,
                          leafItem.shortText || '',
                        )
                }
                ref={
                  leafItem.id === highlightedId
                    ? scrollToItemOnHighlightedRef
                    : leafItem.isSelected
                    ? scrollToItemOnSelectedClickRef
                    : null
                }
                id={'nonColorCardLeaf' + i}
                data-test-element-type={
                  TestElementTypeId.GuidedViewNonColourCard
                }
                data-test-etel-variant-id={leafItem.id}
              >
                <CardTextContainer>
                  <CardTextHeader>
                    {leafItem.shortText
                      ? capitalizeFirstLetter(leafItem.shortText)
                      : leafItem.id}
                  </CardTextHeader>
                  <CardTextWrapper>
                    <CardText>
                      {leafItem.readmoreText?.map((textItem, index) => (
                        <div key={index}>
                          {textItem?.textParagraph ?? ''}
                          {textItem?.textList ? (
                            <div> • &nbsp; {textItem.textList}</div>
                          ) : (
                            ''
                          )}
                        </div>
                      ))}
                    </CardText>
                  </CardTextWrapper>

                  <ReadMoreAndSuperWrapper>
                    {leafItem.hasreadMore ? (
                      <CardReadMore
                        onClick={(e) => handleReadmoreClick(e, leafItem)}
                      >
                        <SvgInfo />
                        {t2(TextId.DESCRIPTION_VIEW_INFORMATION)}
                      </CardReadMore>
                    ) : (
                      <CardReadMore />
                    )}
                    {superEngines &&
                      superEngines.some((i) => i.id === leafItem.id) && (
                        <CardSuper>
                          <SvgScaniaSuper />
                        </CardSuper>
                      )}
                  </ReadMoreAndSuperWrapper>
                </CardTextContainer>
                {leafItem.iconUrl && (
                  <CardImage>
                    <img src={leafItem.iconUrl || undefined} alt="truck" />
                  </CardImage>
                )}
                <CardStatusWrapper>
                  {leafItem.isSelected && <SvgTick />}
                  {currentlyLoadingId === leafItem.id && (
                    <TdsSpinner size="sm" />
                  )}
                </CardStatusWrapper>
                {showFpcCodeOnHover && (
                  <TdsTooltip
                    placement="top"
                    selector={'#nonColorCardLeaf' + i}
                    text={leafItem.id}
                  />
                )}
              </OptionCard>
            )
          })}
      </CardsWrapper>
    </MenuContentRoot>
  )
}
